import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Save } from 'lucide-react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useAccount } from '../../context/AccountContext';
import { TopicMediaPanel } from '../content/TopicMediaPanel';
import {
  GET_ACCOUNT_TAXONOMY,
  GET_ACCOUNT_TOPIC_FIELDS,
  GET_COLLECTION_ITEM,
  SAVE_COLLECTION_ITEM,
} from '../../graphql';
import { Loader } from '../common/PageLoader';

/**
 * One collection item, editable.
 *
 * Everything captured is a draft with whatever metadata was to hand at the
 * copy stand; this is where it becomes catalogued. It writes through
 * `saveCollectionItem` — the same mutation the capture screen uses and the same
 * upsert the studio app's REST path uses — so validation cannot differ
 * depending on which door an edit came through.
 *
 * It deliberately does NOT reuse the Atomizer content editor. That screen is
 * gated on the Atomizer entitlement, and a Collect-only account (which is the
 * whole point of the product split) does not hold it — which is exactly the bug
 * that sent a click on a collection tile to /content/:id and bounced it back.
 */

interface TopicField {
  fieldKey: string;
  label: string;
  description?: string | null;
  fieldType: string;
  required: boolean;
  options?: string[] | null;
  uiGroup?: string | null;
  sortOrder: number;
  isActive: boolean;
}

interface TaxonomyDimension {
  key: string;
  terms: string[];
  isFreeform: boolean;
}

const DIMENSION_LABEL: Record<string, string> = {
  sport: 'Sport',
  league: 'League',
  manufacturer: 'Manufacturer',
  product_set: 'Set',
  team: 'Team',
  player: 'Player',
};

const money = (n: number | null | undefined, currency = 'USD') =>
  n == null ? null : new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(n);

export function CollectionItemDetail() {
  const { id } = useParams<{ id: string }>();
  const { accountId, isLoading: accountsLoading } = useAccount();
  const navigate = useNavigate();

  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [terms, setTerms] = useState<Record<string, string>>({});
  const [status, setStatus] = useState('draft');
  const [dirty, setDirty] = useState(false);

  const { data, loading, error, refetch } = useQuery(GET_COLLECTION_ITEM, {
    variables: { accountId, id },
    skip: !accountId || !id,
    fetchPolicy: 'cache-and-network',
  });

  const { data: fieldData } = useQuery(GET_ACCOUNT_TOPIC_FIELDS, {
    variables: { accountId },
    skip: !accountId,
  });
  const { data: taxData } = useQuery(GET_ACCOUNT_TAXONOMY, {
    variables: { accountId },
    skip: !accountId,
  });

  const [save, { loading: saving }] = useMutation(SAVE_COLLECTION_ITEM);

  const item = data?.collectionItem;

  const templateFields: TopicField[] = useMemo(
    () =>
      [...((fieldData?.accountTopicFields as TopicField[]) ?? [])]
        .filter((f) => f.isActive)
        .sort((a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label)),
    [fieldData]
  );

  const dimensions: TaxonomyDimension[] = useMemo(
    () => (taxData?.accountTaxonomy as TaxonomyDimension[]) ?? [],
    [taxData]
  );

  // Seed the form from the server, once per load of an item. Editing state is
  // local until Save: a half-typed player name should not be racing the
  // cache-and-network refetch.
  useEffect(() => {
    if (!item) return;
    setTitle(item.title ?? '');
    setSummary(item.summary ?? '');
    setStatus(item.publishStatus ?? 'draft');
    const nextFields: Record<string, string> = {};
    for (const [k, v] of Object.entries(item.customFields ?? {})) {
      nextFields[k] = v == null ? '' : String(v);
    }
    setFields(nextFields);
    const nextTerms: Record<string, string> = {};
    for (const [k, list] of Object.entries(item.taxonomies ?? {})) {
      const first = Array.isArray(list)
        ? (list[0] as { term?: string } | string | undefined)
        : undefined;
      nextTerms[k] = typeof first === 'string' ? first : (first?.term ?? '');
    }
    setTerms(nextTerms);
    setDirty(false);
  }, [item]);

  const edit =
    <T,>(setter: (v: T) => void) =>
    (value: T) => {
      setter(value);
      setDirty(true);
    };

  const handleSave = async () => {
    if (!accountId || !item) return;

    const cleanFields: Record<string, string> = {};
    for (const [k, v] of Object.entries(fields)) {
      if (v.trim()) cleanFields[k] = v.trim();
    }
    // Only send dimensions that carry a term. An empty object means "leave as
    // is" server-side, which is what makes a status-only save safe.
    const cleanTaxonomies: Record<string, string[]> = {};
    for (const [k, v] of Object.entries(terms)) {
      if (v.trim()) cleanTaxonomies[k] = [v.trim()];
    }

    try {
      await save({
        variables: {
          accountId,
          input: {
            sku: item.sku,
            title: title.trim() || null,
            summary: summary.trim() || null,
            fields: cleanFields,
            taxonomies: cleanTaxonomies,
            publishStatus: status,
          },
        },
      });
      setDirty(false);
      await refetch();
      toast.success('Saved');
    } catch (err) {
      // Closed dimensions reject unknown terms, so this is the most likely
      // failure and the message names the offending value — surface it whole.
      toast.error(err instanceof Error ? err.message : 'Could not save');
    }
  };

  if (accountsLoading) return <Loader label="Loading" className="p-6 text-cmp-ink-3" />;

  return (
    <div className="ink-page flex w-full flex-col h-full min-h-0 overflow-hidden">
      <div className="flex flex-col flex-1 min-h-0 overflow-y-auto md:overflow-hidden">
        <div className="w-full max-w-full overflow-hidden relative flex-1 flex flex-col">
          <div className="m-0 w-full h-full relative overflow-hidden box-border bg-primary-background flex flex-col">
            <div className="ink-frame">
              <div className="flex-1 min-h-0 overflow-y-auto space-y-5 pr-1">
                <header className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0">
                    <button
                      onClick={() => navigate('/collection')}
                      className="text-sm text-cmp-ink-2 hover:text-cmp-ink inline-flex items-center gap-1"
                    >
                      <ArrowLeft className="w-4 h-4" /> Collection
                    </button>
                    <h1 className="text-2xl font-semibold text-cmp-ink mt-1 truncate">
                      {title || item?.sku || 'Item'}
                    </h1>
                    {item && (
                      <p className="text-xs text-cmp-ink-3 mt-1">
                        {item.sku}
                        {item.listing?.askingPrice != null && (
                          <> · {money(item.listing.askingPrice, item.listing.currency || 'USD')}</>
                        )}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {dirty && (
                      <Badge variant="outline" className="text-[10px]">
                        unsaved
                      </Badge>
                    )}
                    <Button onClick={() => void handleSave()} disabled={saving || !item || !dirty}>
                      {saving ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4" />
                      )}
                      <span className="ml-2">Save</span>
                    </Button>
                  </div>
                </header>

                {error && (
                  <Card className="p-4 text-sm text-red-400 bg-cmp-raise border border-cmp-line">
                    Could not load this item: {error.message}
                  </Card>
                )}

                {!loading && !item && !error && (
                  <Card className="p-10 text-center text-cmp-ink-2 bg-cmp-raise border border-cmp-line text-cmp-ink">
                    That item is not in this collection.
                  </Card>
                )}

                {item && (
                  <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-6 items-start">
                    {/* Photos */}
                    <Card className="p-4 bg-cmp-raise border border-cmp-line text-cmp-ink">
                      <TopicMediaPanel
                        accountId={accountId as string}
                        topicId={item.id}
                        defaultRole="front"
                        onChanged={() => void refetch()}
                      />
                    </Card>

                    {/* Metadata */}
                    <div className="space-y-4 min-w-0">
                      <Card className="p-4 space-y-3 bg-cmp-raise border border-cmp-line text-cmp-ink">
                        <div className="text-sm font-medium">Details</div>

                        <div>
                          <Label htmlFor="itemTitle">Title</Label>
                          <Input
                            id="itemTitle"
                            value={title}
                            onChange={(e) => edit(setTitle)(e.target.value)}
                          />
                        </div>

                        <div>
                          <Label htmlFor="itemSummary">Notes</Label>
                          <Input
                            id="itemSummary"
                            value={summary}
                            onChange={(e) => edit(setSummary)(e.target.value)}
                            placeholder="Notes"
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          {templateFields.map((f) => (
                            <div key={f.fieldKey}>
                              <Label htmlFor={`field-${f.fieldKey}`}>
                                {f.label}
                                {f.required && <span className="text-cmp-ink-3"> *</span>}
                              </Label>
                              {f.options?.length ? (
                                <select
                                  id={`field-${f.fieldKey}`}
                                  value={fields[f.fieldKey] ?? ''}
                                  onChange={(e) =>
                                    edit(setFields)({ ...fields, [f.fieldKey]: e.target.value })
                                  }
                                  className="w-full bg-cmp-surface border border-cmp-line rounded px-2 py-2 text-sm text-cmp-ink"
                                >
                                  <option value="">—</option>
                                  {f.options.map((o) => (
                                    <option key={o} value={o}>
                                      {o}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <Input
                                  id={`field-${f.fieldKey}`}
                                  value={fields[f.fieldKey] ?? ''}
                                  onChange={(e) =>
                                    edit(setFields)({ ...fields, [f.fieldKey]: e.target.value })
                                  }
                                />
                              )}
                              {f.description && (
                                <p className="text-[11px] text-cmp-ink-3 mt-0.5">{f.description}</p>
                              )}
                            </div>
                          ))}
                        </div>
                        {templateFields.length === 0 && (
                          <p className="text-xs text-cmp-ink-3">
                            No card fields defined yet — add them in Settings → Custom Fields.
                          </p>
                        )}
                      </Card>

                      <Card className="p-4 space-y-3 bg-cmp-raise border border-cmp-line text-cmp-ink">
                        <div className="text-sm font-medium">Classification</div>
                        <div className="grid grid-cols-2 gap-3">
                          {dimensions.map((d) => (
                            <div key={d.key}>
                              <Label htmlFor={`dim-${d.key}`}>
                                {DIMENSION_LABEL[d.key] || d.key}
                              </Label>
                              {d.isFreeform ? (
                                <>
                                  <Input
                                    id={`dim-${d.key}`}
                                    list={`dim-list-${d.key}`}
                                    value={terms[d.key] ?? ''}
                                    onChange={(e) =>
                                      edit(setTerms)({ ...terms, [d.key]: e.target.value })
                                    }
                                  />
                                  <datalist id={`dim-list-${d.key}`}>
                                    {d.terms.map((t) => (
                                      <option key={t} value={t} />
                                    ))}
                                  </datalist>
                                </>
                              ) : (
                                // Closed vocabulary: a select, so an invalid term
                                // cannot be typed and rejected on save.
                                <select
                                  id={`dim-${d.key}`}
                                  value={terms[d.key] ?? ''}
                                  onChange={(e) =>
                                    edit(setTerms)({ ...terms, [d.key]: e.target.value })
                                  }
                                  className="w-full bg-cmp-surface border border-cmp-line rounded px-2 py-2 text-sm text-cmp-ink"
                                >
                                  <option value="">—</option>
                                  {d.terms.map((t) => (
                                    <option key={t} value={t}>
                                      {t}
                                    </option>
                                  ))}
                                </select>
                              )}
                            </div>
                          ))}
                        </div>
                        {dimensions.length === 0 && (
                          <p className="text-xs text-cmp-ink-3">
                            No taxonomy defined yet — add dimensions in Settings → Taxonomy.
                          </p>
                        )}
                      </Card>

                      <Card className="p-4 space-y-3 bg-cmp-raise border border-cmp-line text-cmp-ink">
                        <div className="text-sm font-medium">Publishing</div>
                        <div className="flex items-center gap-2 flex-wrap">
                          {[
                            { v: 'draft', label: 'Draft' },
                            { v: 'published', label: 'Published' },
                          ].map((s) => (
                            <button
                              key={s.v}
                              onClick={() => edit(setStatus)(s.v)}
                              className={`px-3 py-1.5 rounded text-sm ${
                                status === s.v
                                  ? 'bg-cmp-bronze-soft text-cmp-ink'
                                  : 'text-cmp-ink-3 hover:text-cmp-ink'
                              }`}
                            >
                              {s.label}
                            </button>
                          ))}
                        </div>
                        <p className="text-[11px] text-cmp-ink-3">
                          Publishing puts this item on your public site, showing only the fields
                          marked &ldquo;show publicly&rdquo; in Custom Fields. Required fields are
                          enforced on publish, not on save as a draft.
                        </p>
                      </Card>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
